import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { usePinnedHeights } from '../ItAssets/ItAssets';
import { kpcApi } from './apiClient';
import { Alert, Ctx, Pill, useList } from './common';
import { TABS, parseTab, tabPath } from './routes';
import Overview from './Overview';
import { ClubsTab, RulesTab } from './MappingTabs';
import ClubsNeedingMapping from './ClubsNeedingMapping';
import StoresStillInvalid from './StoresStillInvalid';
import ChangesTab from './ChangesTab';
import CodistoTab from './CodistoTab';

const LAST = 'kpc.tab';

export default function KlaviyoProfileCleanup() {
  const { tab: tabParam } = useParams();
  const navigate = useNavigate();
  const tab = useMemo(() => parseTab(tabParam), [tabParam]);
  useEffect(() => {
    if (tab) { try { localStorage.setItem(LAST, tab); } catch { /* per-browser only */ } return; }
    let last = 'overview';
    try { last = parseTab(localStorage.getItem(LAST)) || 'overview'; } catch { /* then overview */ }
    navigate(tabPath(last), { replace: true });
  }, [tab, navigate]);
  const { data: overview, error, reload } = useList(() => kpcApi.overview(), []);
  const [toast, setToast] = useState('');
  useEffect(() => { if (!toast) return undefined; const t = setTimeout(() => setToast(''), 3500); return () => clearTimeout(t); }, [toast]);
  useEffect(() => { if (!overview || !overview.running) return undefined; const t = setInterval(reload, 5000); return () => clearInterval(t); }, [overview, reload]);
  const setPage = usePinnedHeights();
  const ctx = useMemo(() => ({ overview, reloadOverview: reload, notify: setToast }), [overview, reload]);
  if (!tab) return null;
  const last = overview && overview.last ? overview.last : [];
  const fixed = last.reduce((n, r) => n + ((r.counts || {}).updated || 0) + ((r.counts || {}).from_order || 0) + ((r.counts || {}).from_pickup || 0) + ((r.counts || {}).from_club || 0) + ((r.counts || {}).from_rule || 0), 0);
  const deleted = last.reduce((n, r) => n + ((r.counts || {}).deleted || 0), 0);
  const failed = last.some((r) => r.status === 'failed');
  return (
    <Ctx.Provider value={ctx}>
      <section className="uh-page uh-ia-page" ref={setPage}>
        <div className="uh-ia-top">
          <div className="uh-ph">
            <div>
              <h1 className="uh-h1">Klaviyo Profile Cleanup</h1>
              <div className="uh-sub">Keeps every profile's <span className="uh-mono">Local Store</span> a real store, and removes bounced Codisto addresses. Runs nightly at 2:00 AM ET.</div>
            </div>
            {overview && (
              <div className="uh-ia-stats">
                {[[fixed, last.length && last[0].dry_run ? 'Would fix (dry run)' : 'Fixed last run'], [overview.invalid.count, 'Stores still invalid'], [overview.rules, 'Rules'], [deleted, 'Codisto deleted']].map(([v, k]) => (
                  <div className="stat" key={k}><span className="v uh-num">{Number(v).toLocaleString()}</span><span className="k">{k}</span></div>
                ))}
                <div className="stat"><span className="v">{overview.running ? <Pill tone="hold">Running</Pill> : failed ? <Pill tone="bad">Failed</Pill> : <Pill tone="ful">Healthy</Pill>}</span><span className="k">{last.length ? `Last run ${new Date(last[0].started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })}` : 'No run yet'}</span></div>
              </div>
            )}
          </div>
          <div className="uh-ia-tabs" role="tablist">
            {TABS.map(([id, label]) => <button key={id} type="button" role="tab" aria-selected={tab === id} className="uh-ia-tab" onClick={() => navigate(tabPath(id))}>{label}</button>)}
          </div>
        </div>
        <Alert>{error}</Alert>
        {tab === 'overview' && <Overview />}
        {tab === 'rules' && <RulesTab />}
        {tab === 'clubs' && <ClubsTab />}
        {tab === 'stores' && <StoresStillInvalid />}
        {tab === 'invalid' && <ClubsNeedingMapping />}
        {tab === 'changes' && <ChangesTab />}
        {tab === 'codisto' && <CodistoTab />}
        {toast && <div className="uh-ia-toast" role="status">{toast}</div>}
      </section>
    </Ctx.Provider>
  );
}
